import { useApolloClient } from '@apollo/client';
import React, { useState } from 'react';

import { createLoanTapeEvaluator } from '@finley-technologies/calc-agent';
import { csvConversion, finleyAssert } from '@finley-technologies/common';
import {
  Util,
  Receivable as ReceivableTypes,
} from '@finley-technologies/finley-types';
import { TapeBuilderUI } from '@finley-technologies/tape-builder';

import {
  getIncomingGenerateLoanTapeNode,
  getLoanProductSlug,
  useGraph,
} from '../GraphContext';
import { getLoanTapeMetadataExtractor } from './getLoanTapeMetadataExtractor';
import { ProgressWithDownloadButton } from './ProgressWithDownloadButton';
import { useQAContext } from './QAContext';
import { CreditAgreement } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';

export type CFReceivableWithRow = ReceivableTypes.CreditFacility.CreditFacilityReceivable & {
  sanitizedRow: Util.SanitizedCsvRow;
};

type Props = {
  loanProductMechanics: CreditAgreement.Config.ResolvedLoanProductMechanics;
  creditFacilityId: string;
  tapeBuilderNode: TapeBuilderUI.types.OperationNodes.CalcAgent.UINode;
  reportingDate: Util.FullDateStr;
  fileVersion: number;
  orgSlugMap: { [orgSlug: string]: { slug: string; id: string } };
  onDone: (receivables: CFReceivableWithRow[]) => void;
};
export const CreditFacilityReceivableEvaluator = ({
  loanProductMechanics,
  creditFacilityId,
  reportingDate,
  fileVersion,
  tapeBuilderNode,
  orgSlugMap,
  onDone,
}: Props) => {
  const apolloClient = useApolloClient();
  const graphContext = useGraph();
  const { getNodeBuffer } = useQAContext();
  const loanProductSlug = getLoanProductSlug(
    graphContext,
    loanProductMechanics.loanProductId,
  );
  finleyAssert(
    loanProductSlug,
    `loanProductSlug not found for ${loanProductMechanics.loanProductId}`,
  );

  const [progressPercent, setProgressPercent] = useState(0);
  const [progressText, setProgressText] = useState('');

  const generateCreditFacilityReceivables = async () => {
    const generatedLoanProductNode = getIncomingGenerateLoanTapeNode({
      tapeBuilderNode,
      graphContext,
      loanProductSlug,
    });

    finleyAssert(
      generatedLoanProductNode,
      'generatedLoanProductNode not found',
    );

    const loanTapeMetadataExtractor = getLoanTapeMetadataExtractor({
      apolloClient,
      creditFacilityId,
      loanProductId: loanProductMechanics.loanProductId,
      reportingDate,
      fileVersion,
    });

    const loanTapeEvaluator = await createLoanTapeEvaluator({
      config: loanProductMechanics,
      loanTapeMetadataExtractor,
      loanTapeType: generatedLoanProductNode.configuration.loanTapeType,
      orgSlugMap,
      creditFacilityId,
      f1EngineMode: undefined, // TODO: @sachinr4 - 2Fast2Formula
    });

    const buffer = getNodeBuffer(
      generatedLoanProductNode.metadata.tapeBuilderNodeId,
    );

    finleyAssert(
      buffer,
      'Intermediate Calcs Failed. Please upload dependencies',
    );

    // for preassigned master tapes, we need to filter receivables using assignment config
    // from the generatedLoanTape node.
    const {
      externalAssignmentField,
      signaledAssignmentValueMapping,
    } = generatedLoanProductNode.configuration;

    let assignmentFilter: (rec: Util.SanitizedCsvRow) => boolean = () => true;
    if (externalAssignmentField && signaledAssignmentValueMapping) {
      // find assignment config from the generatedLoanTape node
      const cfSlug = tapeBuilderNode.configuration.creditFacilitySlug;
      const assignmentConfig = signaledAssignmentValueMapping.find(
        (a) => a.creditFacilitySlug === cfSlug,
      );
      if (assignmentConfig) {
        assignmentFilter = (rec) =>
          rec[externalAssignmentField] === assignmentConfig.fieldValue;
      }
    }

    const {
      errors,
      creditFacilityReceivableWithoutFinleyId,
    } = await loanTapeEvaluator.convertToCreditFacilityReceivables({
      csv: buffer,
      assignmentFilter,
      progressCallback: async (
        operation: string,
        strProgress: string,
        percent: number,
      ) => {
        setProgressText(`${operation}: ${strProgress}`);
        setProgressPercent(percent);
        await new Promise((resolve) => setTimeout(resolve, 0));
      },
      csvParser: csvConversion.smallCsvParser,
    });
    setProgressPercent(100);
    setProgressText('Done!');
    if (errors.length) {
      const errorText = errors.map((e) => e.message).join(',');
      finleyAssert(false, errorText);
    }
    const cfReceivables: CFReceivableWithRow[] = creditFacilityReceivableWithoutFinleyId.map(
      (rec) => {
        return {
          ...rec,
          finleyReceivableId: `finley_${rec.externalReceivableId}`,
          assignmentFields: null, // QA does not need to know about assignment fields
        };
      },
    );
    onDone(cfReceivables);
    return cfReceivables;
  };

  const filename = `${loanProductSlug}_receivables.json`;
  return (
    <ProgressWithDownloadButton
      onStart={generateCreditFacilityReceivables}
      buttonText={`Intermediate Calcs: ${loanProductSlug}`}
      progressPercent={progressPercent}
      progressText={progressText}
      filename={filename}
    />
  );
};
